'use client';

import React, { useMemo, useEffect, useRef, useCallback } from 'react';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { LOCATION } from 'src/hooks/filters/constants';
import useStrategies from 'src/hooks/strategy/useStrategies';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useGetStrategyMetricsQuery, useGetMyChainedStrategiesQuery } from 'src/api/redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Drawer } from 'src/components';
import { IconButton } from 'src/components/PulseUI/IconButton';
import {
  getTargetStrategyIdString,
  resolveEncountersDisplayOptions,
  resolveEncountersMetricCode,
} from 'src/utils/strategyHelpers';

export const HEATMAP_DRAWER_ID = 'strategy-heatmap-metrics';

const HeatmapControls: React.FC = () => {
  const {
    selectedChainIds,
    selectedTerritory,
    showHeatmap,
    setShowHeatmap,
    selectedHeatmapMetric,
    setSelectedHeatmapMetric,
  } = useStrategies(LOCATION.STRATEGY_CENTER);
  const { isDrawerOpen, closeDrawer } = useDrawer();
  const { showEncountersTotals } = useFlags();

  const isOpen = isDrawerOpen(HEATMAP_DRAWER_ID);

  const { data: chainedStrategies } = useGetMyChainedStrategiesQuery();
  const selectedChain = useMemo(() => {
    if (selectedChainIds.length !== 1) return null;
    return chainedStrategies?.results?.find(
      (s: any) => s.id === selectedChainIds[0]
    ) ?? null;
  }, [selectedChainIds, chainedStrategies?.results]);
  const strategyId = getTargetStrategyIdString(selectedChain);

  // Fetch strategy metrics to determine the correct sort field
  const { data: strategyMetrics } = useGetStrategyMetricsQuery(
    {
      strategyType: 'target',
      strategyId: strategyId || '',
    },
    {
      skip: !strategyId,
    }
  );

  // Flatten metrics for easier access, including direction in display label
  const allFlatMetrics =
    strategyMetrics?.results?.flatMap((metric) => {
      const direction = metric.execution_params?.direction;
      const displayLabel = direction
        ? `${metric.label} (${direction})`
        : metric.label;

      return metric.display_options.map((option) => ({
        ...option,
        metricLabel: displayLabel,
        metricId: metric.id,
      }));
    }) || [];

  // All option codes (incl. encounters) drive the claims → encounters swap.
  // Memoized so it is a stable effect dependency.
  const allOptionCodes = useMemo(
    () =>
      (strategyMetrics?.results ?? []).flatMap((metric) =>
        metric.display_options.map((option) => option.code)
      ),
    [strategyMetrics?.results]
  );
  // Never render a standalone "Encounters" heatmap option; the claims option is
  // relabeled "Encounters" when the flag is on, matching the value it reads. Pin the
  // element type so the metricLabel/metricId fields survive (generic would fall back
  // to the constraint).
  const flatMetrics = resolveEncountersDisplayOptions<
    (typeof allFlatMetrics)[number]
  >(allFlatMetrics, !!showEncountersTotals);

  // The metric code to actually request for a given (claims) option — swaps to the
  // encounters sibling when the flag is on and it exists.
  const requestCodeFor = (code: string) =>
    resolveEncountersMetricCode(code, allOptionCodes, !!showEncountersTotals);

  // Keep an already-selected heatmap metric aligned with the flag: when LaunchDarkly
  // resolves/changes after a metric is selected, re-point the stored code so the map
  // requests the correct claims/encounters metric without waiting for a re-select.
  useEffect(() => {
    const current = selectedHeatmapMetric?.code;
    if (!current) return;
    let desired = current;
    if (showEncountersTotals) {
      desired = resolveEncountersMetricCode(current, allOptionCodes, true) ?? current;
    } else if (current.endsWith('_encounters')) {
      const claims = current.replace(/_encounters$/, '_claims');
      if (allOptionCodes.includes(claims)) desired = claims;
    }
    if (desired !== current) {
      setSelectedHeatmapMetric({ code: desired, label: selectedHeatmapMetric?.label });
    }
  }, [
    showEncountersTotals,
    allOptionCodes,
    selectedHeatmapMetric?.code,
    selectedHeatmapMetric?.label,
    setSelectedHeatmapMetric,
  ]);

  // Group metrics by their id to avoid duplicate label collisions
  type GroupedMetric = { label: string; options: typeof flatMetrics };
  const groupedItems: Record<number, GroupedMetric> = {};
  flatMetrics.forEach((item) => {
    const key = item.metricId;
    if (!groupedItems[key]) {
      groupedItems[key] = { label: item.metricLabel, options: [] };
    }
    groupedItems[key].options.push(item);
  });

  const handleSelectMetric = (opt: (typeof flatMetrics)[0]) => {
    // Request the encounters sibling when the flag is on; `opt.label` already reads
    // "Encounters" in that case, so the stored label matches the requested metric.
    const requestCode = requestCodeFor(opt.code);
    const isCurrentlySelected = selectedHeatmapMetric?.code === requestCode;
    if (isCurrentlySelected) {
      setSelectedHeatmapMetric(null);
      setShowHeatmap(false);
    } else {
      setSelectedHeatmapMetric({ code: requestCode, label: opt.label });
      setShowHeatmap(true);
    }
    handleClose();
  };

  const handleSelectAllOpportunities = () => {
    setShowHeatmap(true);
    setSelectedHeatmapMetric(null);
    handleClose();
  };

  // Check if "All Opportunities" is currently selected (heatmap on, no metric)
  const isAllOpportunitiesSelected = showHeatmap && !selectedHeatmapMetric;

  const handleTurnOffHeatmap = () => {
    setShowHeatmap(false);
    setSelectedHeatmapMetric(null);
    handleClose();
  };

  const handleClose = useCallback(() => closeDrawer(HEATMAP_DRAWER_ID), [closeDrawer]);

  const drawerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, handleClose]);

  const hasMultipleStrategies = selectedChainIds.length > 1;
  const hasAllTerritories = !selectedTerritory;

  return (
    <div ref={drawerRef}>
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      anchor="left"
      size="sm"
      variant="persistent"
      className="z-50 absolute left-0 top-0 bottom-0 h-full"
      data-testid="heatmap-controls-drawer"
      aria-label="Heatmap metrics selection"
    >
      <Drawer.Header className="bg-neutral-50 border-b border-neutral-200">
        <div className="flex items-center justify-between w-full">
          <h3
            id="heatmap-drawer-title"
            className="text-sm font-semibold text-neutral-800"
          >
            Heatmap Metrics
          </h3>
          <IconButton
            variant="plain"
            color="neutral"
            size="sm"
            onClick={handleClose}
            icon={faXmark}
            aria-label="Close heatmap metrics drawer"
            data-testid="heatmap-close-button"
          />
        </div>
      </Drawer.Header>

      <Drawer.Content className="bg-white">
        {hasMultipleStrategies ? (
          <div
            className="px-4 py-8 text-center text-neutral-500 text-sm"
            data-testid="heatmap-multi-strategy-notice"
            role="status"
          >
            Heatmap is not available when multiple strategies are selected.
          </div>
        ) : hasAllTerritories ? (
          <div
            className="px-4 py-8 text-center text-neutral-500 text-sm"
            data-testid="heatmap-all-territories-notice"
            role="status"
          >
            Heatmap is not available when all territories are selected. Please select a specific territory.
          </div>
        ) : (
        <div role="listbox" aria-labelledby="heatmap-drawer-title">
          {/* All Opportunities option at the top */}
          <div className="border-b border-neutral-100">
            <button
              onClick={handleSelectAllOpportunities}
              role="option"
              aria-selected={isAllOpportunitiesSelected}
              data-testid="heatmap-all-opportunities-button"
              className={`
                w-full px-4 py-3 text-left text-sm transition-colors
                ${
                  isAllOpportunitiesSelected
                    ? 'bg-sky-50 text-sky-600 font-medium border-l-2 border-sky-500'
                    : 'text-neutral-700 hover:bg-neutral-50 border-l-2 border-transparent'
                }
              `}
            >
              All Opportunities
            </button>
          </div>

          {/* Metric options */}
          {Object.entries(groupedItems).map(([metricId, { label, options }]) => (
            <div
              key={metricId}
              className="border-b border-neutral-100"
              role="group"
              aria-label={label}
            >
              <div className="px-4 py-2 text-xs font-semibold text-neutral-500 uppercase tracking-wide bg-neutral-50">
                {label}
              </div>
              {options.map((opt) => (
                <button
                  key={opt.code}
                  onClick={() => handleSelectMetric(opt)}
                  role="option"
                  aria-selected={
                    selectedHeatmapMetric?.code === requestCodeFor(opt.code)
                  }
                  data-testid={`heatmap-metric-${opt.code}`}
                  className={`
                    w-full px-4 py-3 text-left text-sm transition-colors
                    ${
                      selectedHeatmapMetric?.code === requestCodeFor(opt.code)
                        ? 'bg-sky-50 text-sky-600 font-medium border-l-2 border-sky-500'
                        : 'text-neutral-700 hover:bg-neutral-50 border-l-2 border-transparent'
                    }
                  `}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          ))}

          {/* Empty state - only show if no metrics AND not showing all opportunities option */}
          {Object.keys(groupedItems).length === 0 && (
            <div
              className="px-4 py-8 text-center text-neutral-500 text-sm"
              data-testid="heatmap-empty-state"
            >
              No metrics available
            </div>
          )}
        </div>
        )}
      </Drawer.Content>

      {/* Footer with Turn Off button - only show when heatmap is active */}
      {showHeatmap && (
        <Drawer.Footer className="bg-neutral-50 border-t border-neutral-200 px-4 py-3">
          <button
            onClick={handleTurnOffHeatmap}
            aria-label="Turn off heatmap"
            data-testid="heatmap-turn-off-button"
            className="w-full px-4 py-2 text-sm font-medium text-neutral-600 bg-white border border-neutral-300 rounded-full hover:bg-neutral-50 transition-colors"
          >
            Turn Off Heatmap
          </button>
        </Drawer.Footer>
      )}
    </Drawer>
    </div>
  );
};

export default HeatmapControls;
